import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Radio,
    Button,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import ListIcon from "@material-ui/icons/List";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { getAppColor } from "@icarius-app/selectors";
import SelectionDialog from "@icarius-common/selectionDialog";
import { roles } from "@icarius-utils/constants";
import { getModulesTreeAction } from "@icarius-pages/modulesAndMenus/actions";
import ModulesAndMenusTree from "../../modules/treeDialog/modulesAndMenusTree";
import GridSelectionDialog from "../gridSelectionDialog";
import useHandleForm from "./useHandleForm";
import ReadOnlyInput from "@icarius-common/dynamicInputs/readOnlyInput";
import { Alert } from "@material-ui/lab";

const ExclusionsFormDialog = (props) => {
    const { open, data, moduleList, tree, collaboratorsList, submitCallback, handleClose, isLoading } = props;

    const color = useSelector(getAppColor);
    const dispatch = useDispatch();

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };

    const { isCreate, formData, setFormValue, submit } = useHandleForm(data, submitCallback, openValidationError);

    const [role, setRole] = useState(data?.role || "E");
    const [isTypeModule, setIsTypeModule] = useState(isCreate ? true : Boolean(data?.module));
    const [userSelectionDialogIsOpen, setUserSelectionDialogIsOpen] = useState(false);
    const [gridSelectionDialogIsOpen, setGridSelectionDialogIsOpen] = useState(false);

    useEffect(() => {
        dispatch(getModulesTreeAction(role, ""));
    }, [dispatch, role]);

    const menuName = useMemo(() => {
        if (!formData.menu) return "";
        if (!tree?.modules?.length) return "";

        let auxName = "";
        const searchInBranch = (branch) => {
            branch.forEach((item) => {
                if (item.id === formData.menu) {
                    auxName = item.name;
                } else {
                    if (item.menus) {
                        searchInBranch(item.menus);
                    }
                }
            });
        };

        searchInBranch(tree.modules);
        return auxName;
    }, [tree.modules, formData.menu]);

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={DialogTransition}
                PaperComponent={PaperDraggable}
                maxWidth={"sm"}
                fullWidth={true}
            >
                <div className={"dialog-container"}>
                    <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                        {getLocalizedString(isCreate ? "createExclusion" : "updateExclusion")}
                        <DialogTitleDivider />
                    </DialogTitle>
                    <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
                    <DialogContent style={{ paddingTop: 0 }}>
                        <Loader open={isLoading} />
                        <Grid container item xs={12} spacing={2}>
                            <Grid container item alignItems="center" xs={12}>
                                <Grid container item xs={12}>
                                    <Typography
                                        className="whiteText"
                                        variant="subtitle1"
                                        style={{ display: "inline-block", marginRight: 20 }}
                                    >
                                        {getLocalizedString("applyExclusionTo")}
                                    </Typography>
                                </Grid>
                                <Grid container item xs={12}>
                                    <div
                                        style={{
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            marginRight: 20,
                                        }}
                                        onClick={() => {
                                            setIsTypeModule(true);
                                            setFormValue("", "menu");
                                        }}
                                    >
                                        <Radio color="primary" checked={isTypeModule} />
                                        <Typography className="whiteText">{getLocalizedString("module")}</Typography>
                                    </div>
                                    <div
                                        style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                        onClick={() => {
                                            setIsTypeModule(false);
                                            setFormValue("", "module");
                                        }}
                                    >
                                        <Radio color="primary" checked={!isTypeModule} />
                                        <Typography className="whiteText">{getLocalizedString("menu")}</Typography>
                                    </div>
                                </Grid>
                            </Grid>
                            {isTypeModule && (
                                <Grid container item alignItems="center" xs={12}>
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel required id={`label-module`}>
                                            {getLocalizedString("module")}
                                        </InputLabel>
                                        <Select
                                            value={formData["module"]}
                                            labelId={`label-module`}
                                            id={`select-module`}
                                            onChange={(e) => setFormValue(e.target.value, "module")}
                                            margin={"none"}
                                        >
                                            {moduleList.map((item) => (
                                                <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                                    {item.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {!isTypeModule && (
                                <>
                                    <Grid container item xs={12}>
                                        <FormControl style={{ width: "100%" }}>
                                            <InputLabel id={"label-role"}>{getLocalizedString("role")}</InputLabel>
                                            <Select
                                                value={role}
                                                labelId={"label-role"}
                                                id={"select-role"}
                                                onChange={(e) => {
                                                    setRole(e.target.value);
                                                    setFormValue("", "menu");
                                                }}
                                                margin={"none"}
                                            >
                                                {Object.keys(roles)
                                                    .map((roleName) => ({
                                                        key: roles[roleName],
                                                        value: getLocalizedString(roleName),
                                                    }))
                                                    .map((item) => (
                                                        <MenuItem
                                                            className={"whiteText"}
                                                            key={item.key}
                                                            value={item.key}
                                                        >
                                                            {item.value}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid container item xs={12} direction="column">
                                        <ReadOnlyInput
                                            label={
                                                <>
                                                    {getLocalizedString("menu")}
                                                    <span
                                                        style={{ fontWeight: 400, fontSize: 16, marginLeft: 5 }}
                                                        className="errorColor"
                                                    >
                                                        {"*"}
                                                    </span>
                                                </>
                                            }
                                            value={menuName}
                                        />
                                        {Boolean(tree?.modules?.length) && (
                                            <div
                                                style={{
                                                    marginTop: 15,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 10,
                                                    width: "100%",
                                                }}
                                            >
                                                <Alert variant="filled" severity="info">
                                                    {getLocalizedString("clickToSelectMenu")}
                                                </Alert>
                                                <ModulesAndMenusTree
                                                    data={tree}
                                                    selectedIds={[formData.menu]}
                                                    handleClick={(newMenu) => setFormValue(newMenu.id, "menu")}
                                                />
                                            </div>
                                        )}
                                    </Grid>
                                </>
                            )}
                            <Grid container item alignItems="center" xs={12}>
                                <Typography className="whiteText">
                                    {getLocalizedString("employees")}
                                    {Boolean(!formData.sqlFilter) && (
                                        <span
                                            style={{ fontWeight: 400, fontSize: 16, marginLeft: 5 }}
                                            className="errorColor"
                                        >
                                            {"*"}
                                        </span>
                                    )}
                                </Typography>
                                <Grid
                                    container
                                    item
                                    justify="center"
                                    xs={12}
                                    spacing={1}
                                    style={{ margin: "10px 0px" }}
                                >
                                    <Grid container item xs={12} md={9} justify="center">
                                        <Button
                                            variant="contained"
                                            className="whiteText"
                                            onClick={() => setUserSelectionDialogIsOpen(true)}
                                            startIcon={<CheckBoxIcon htmlColor={color} />}
                                            fullWidth
                                        >
                                            {getLocalizedString("selectCollaborators")}
                                        </Button>
                                    </Grid>
                                    <Grid container item xs={12} md={9} justify="center">
                                        <Button
                                            variant="contained"
                                            className="whiteText"
                                            onClick={() => setGridSelectionDialogIsOpen(true)}
                                            startIcon={<ListIcon htmlColor={color} />}
                                            fullWidth
                                        >
                                            {getLocalizedString("collaboratorList")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container item alignItems="center" xs={12}>
                                <TextFieldMultiline
                                    required={!formData.codemps?.length}
                                    fullWidth
                                    rows={5}
                                    margin={"none"}
                                    label={getLocalizedString("visualizationFilter")}
                                    value={formData.sqlFilter}
                                    onChange={(e) => setFormValue(e.target.value, "sqlFilter")}
                                    helperText={
                                        <span style={{ whiteSpace: "pre-wrap" }}>
                                            {getLocalizedString("exclusionSQLHelperText")}
                                        </span>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
                        <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
                    </DialogActions>
                </div>
            </Dialog>
            {userSelectionDialogIsOpen && (
                <SelectionDialog
                    open={userSelectionDialogIsOpen}
                    initialData={formData.codemps}
                    items={collaboratorsList.map((item) => ({
                        ...item,
                        key: item["CODIGO DE EMPLEADO"],
                        value: item["APELLIDO Y NOMBRE"],
                    }))}
                    getItemAvatar={(item) => item["FOTOGRAFIA"]}
                    title={getLocalizedString("selectCollaborators")}
                    selectedTitle={getLocalizedString("selected")}
                    handleClose={() => setUserSelectionDialogIsOpen(false)}
                    handleSubmit={(data) => setFormValue(data, "codemps")}
                />
            )}
            {gridSelectionDialogIsOpen && (
                <GridSelectionDialog
                    open={gridSelectionDialogIsOpen}
                    preselected={formData.codemps}
                    data={collaboratorsList}
                    handleClose={() => setGridSelectionDialogIsOpen(false)}
                    handleConfirm={(data) => setFormValue(data, "codemps")}
                />
            )}
        </>
    );
};

export default ExclusionsFormDialog;
